/**
 * Client-side Amplitude tracking utility
 *
 * This module provides functions to track events to Amplitude on the client side,
 * using the anonymousId, ampDeviceId, and ampSessionId from localStorage.
 */

const AMPLITUDE_API_KEY = '0dcefa00cc091fb76f3a75d4ec0107f6';
const AMPLITUDE_API_URL = 'https://api2.amplitude.com/2/httpapi';

/**
 * Get tracking identifiers from localStorage
 */
function getTrackingIdentifiers() {
    return {
        anonymousId: localStorage.getItem('anonymousId') || undefined,
        ampDeviceId: localStorage.getItem('ampDeviceId') || undefined,
        ampSessionId: localStorage.getItem('ampSessionId') || undefined,
    };
}

/**
 * Track an event to Amplitude
 *
 * @param {string} eventName - The name of the event to track
 * @param {object} eventProperties - Additional properties to include with the event
 * @returns {Promise<void>}
 */
export async function trackAmplitudeEvent(eventName, eventProperties = {}) {
    // Skip tracking in development or if no API key
    if (import.meta.env.DEV || !AMPLITUDE_API_KEY) {
        console.log('[Amplitude] Would track event:', eventName, eventProperties);
        return;
    }

    const { anonymousId, ampDeviceId, ampSessionId } = getTrackingIdentifiers();

    // Build the event payload
    const event = {
        event_type: eventName,
        event_properties: eventProperties,
        time: Date.now(),
    };

    // Add user identification
    if (anonymousId) {
        event.user_id = anonymousId;
    }

    // Add device ID if available
    if (ampDeviceId) {
        event.device_id = ampDeviceId;
    }

    // Add session ID if available
    if (ampSessionId) {
        event.session_id = parseInt(ampSessionId, 10);
    }

    // Add user agent and platform info
    event.platform = 'Web';
    event.os_name = navigator.platform;
    event.language = navigator.language;

    try {
        const response = await fetch(AMPLITUDE_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                api_key: AMPLITUDE_API_KEY,
                events: [event],
            }),
        });

        if (!response.ok) {
            console.error('[Amplitude] Failed to track event:', response.statusText);
        }
    } catch (error) {
        console.error('[Amplitude] Error tracking event:', error);
    }
}

/**
 * Track onboarding step completion
 *
 * @param {string} stepName - The name of the onboarding step
 * @param {object} stepData - Additional data about the step
 */
export function trackOnboardingStep(stepName, stepData = {}) {
    return trackAmplitudeEvent('Onboarding Step Completed', {
        step: stepName,
        ...stepData,
    });
}

